<template>
    <Card>
        <template #header>
            <div class="pl-5 pt-4 font-medium text-[16px]">
                Komentarai
            </div>
        </template>
        <template #content>
            <div class="flex flex-col gap-4">
                <template v-if="commentsExists">
                    <div
                        v-for="comment in comments"
                        :key="comment.id"
                        class="flex flex-col bg-gray-100 p-3 rounded gap-2"
                    >
                        <div class="flex flex-row items-center gap-2">
                            <PVButton
                                v-if="canManagePartnerVisibility"
                                type="button"
                                icon="pi pi-users"
                                severity="info"
                                outlined
                                size="small"
                                class="!w-7 !h-7 !rounded-md !border !border-sky-500 !text-sky-600 hover:!bg-sky-50"
                                title="Komentaro matomumas partneriams"
                                @click.stop.prevent="openVisibilityPopover($event, comment.id)"
                            />

                            <div v-if="comment.title" class="text-black font-bold">
                                {{ comment.title }}
                            </div>
                            <div v-else class="text-black font-bold">Komentaras</div>
                        </div>

                        <div class="text-black text-[13px]" v-if="canViewCommentBody">
                            {{ comment.body }}
                        </div>

                        <div class="flex flex-row items-center text-[13px]">
                            <template v-if="canViewCommentAuthor">{{ comment.author }} - </template
                            >{{ comment.created_at }}

                            <div v-if="canUploadCommentFiles">
                                <FileUpload
                                    ref="fileUploadHandle"
                                    mode="basic"
                                    customUpload
                                    accept=".pdf,.csv,.txt,.doc,.docx,image/*"
                                    :maxFileSize="52428800"
                                    @uploader="(event: any) => customUploader(event, comment.id)"
                                    chooseIcon="pi pi-upload"
                                    :chooseButtonProps="{
                                        size: 'small',
                                        class: '!text-black !text-[12px] !px-2 !py-1 ml-2',
                                        label: 'Įkelti failą',
                                        severity: 'contrast',
                                        variant: 'outlined'
                                    }"
                                    :auto="true"
                                />
                            </div>
                        </div>

                        <div
                            v-if="comment.files.length && canViewCommentFiles"
                            class="flex flex-col items-start gap-2"
                        >
                            <div
                                v-for="file in comment.files"
                                :key="file.id"
                                class="p-1 border border-gray-300 rounded flex flex-row items-center gap-2 text-center relative cursor-pointer text-[13px]"
                                @click.prevent="openFile(comment.id, file)"
                            >
                                <div class="flex flex-row justify-center items-center gap-2">
                                    <i class="pi pi-file !text-[14px]"></i>

                                    {{ file.name }}
                                </div>

                                <div
                                    v-if="canDeleteCommentFiles"
                                    class="flex bg-red-500 hover:bg-red-600 text-white p-[8px] rounded items-center justify-center cursor-pointer"
                                    @click.stop.prevent="deleteFile(comment.id, file.id)"
                                >
                                    <i class="pi pi-trash !text-[0.8rem]"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
                <template v-else>
                    Komentarų nėra
                </template>

                <div class="flex flex-col gap-2 w-full max-w-[800px]" v-if="canWriteComments">
                    <Input :label="''" placeholder="Pavadinimas" v-model="commentTitle"> </Input>
                    <Textarea
                        placeholder="Komentaras"
                        v-model="commentContent"
                        class="w-full"
                        size="small"
                        autoResize
                    />

                    <Button
                        label="Išsaugoti komentarą"
                        size="small"
                        @click="saveComment"
                        :loading="commentSaving"
                        class="self-start"
                    />
                </div>
            </div>
        </template>
    </Card>

    <Popover ref="visibilityPopover" v-if="canManagePartnerVisibility">
        <div class="flex flex-col gap-2 w-[340px]">
            <div class="text-sm font-medium">Komentaro matomumas partneriams</div>
            <MultiSelect
                v-model="activeCommentVisibilitySelection"
                :options="partnerUsers"
                optionLabel="name"
                optionValue="code"
                display="chip"
                placeholder="Pasirinkite partnerius"
                class="w-full"
                size="small"
                @change="updateActiveCommentVisibility"
            />
        </div>
    </Popover>
</template>

<script setup lang="ts">
// System imports
import { ref, onMounted, computed, watch } from 'vue'
import { apiRequest } from '@/services/axios.ts'
import { is } from '@/extensions/permissions.ts'
import { openProtectedFile } from '@/services/fileDownload.ts'

// Local components imports
import Input from '@/components/inputs/Input.vue'

// PrimeVue components imports
import { useConfirm } from 'primevue/useconfirm'
import Textarea from 'primevue/textarea'
import FileUpload from 'primevue/fileupload'
import Card from 'primevue/card'
import Button from 'primevue/button'
import MultiSelect from 'primevue/multiselect'
import Popover from 'primevue/popover'
import PVButton from 'primevue/button'

// Refs and variables
const confirm = useConfirm()
const fileUploadHandle = ref()
const comments = ref<any[]>([])
const commentsExists = ref<boolean>(false)
const commentTitle = ref()
const commentContent = ref()
const commentSaving = ref()
const partnerUsers = ref<Array<{ code: number; name: string }>>([])
const canManagePartnerVisibility = ref<boolean>(false)
const canWriteComments = ref<boolean>(false)
const canUploadCommentFiles = ref<boolean>(false)
const canDeleteCommentFiles = ref<boolean>(false)
const commentVisibilitySelection = ref<Record<number, number[]>>({})
const visibilityPopover = ref()
const activeVisibilityCommentId = ref<number | null>(null)

const canViewCommentBody = computed(() => is('manager|admin|partner_collab|partner_readonly'))
const canViewCommentAuthor = computed(() => canViewCommentBody.value)
const canViewCommentFiles = computed(() => canViewCommentBody.value)

type CommentFile = {
    id: number
    name: string
}

// Emits
const emit = defineEmits<{
    (e: 'draft-changed', value: boolean): void
}>()

// Props
let props = defineProps<{
    contact: any
}>()

const activeCommentVisibilitySelection = computed<number[]>({
    get() {
        if (activeVisibilityCommentId.value === null) {
            return []
        }

        return commentVisibilitySelection.value[activeVisibilityCommentId.value] ?? []
    },
    set(value: number[]) {
        if (activeVisibilityCommentId.value === null) {
            return
        }

        commentVisibilitySelection.value[activeVisibilityCommentId.value] = Array.isArray(value)
            ? value
            : []
    }
})

const hasDraft = computed(() => {
    const title = typeof commentTitle.value === 'string' ? commentTitle.value.trim() : ''
    const body = typeof commentContent.value === 'string' ? commentContent.value.trim() : ''

    return title !== '' || body !== ''
})

// Hooks
onMounted(() => {
    loadComments()
})

watch(
    hasDraft,
    (value) => {
        emit('draft-changed', value)
    },
    { immediate: true }
)

// Methods
async function saveComment() {
    await apiRequest('post', '/contacts/' + props.contact.id + '/comments', {
        title: commentTitle.value,
        body: commentContent.value
    })

    commentTitle.value = null
    commentContent.value = null

    await loadComments()
}

async function loadComments() {
    const response = await apiRequest('get', '/contacts/' + props.contact.id + '/comments')

    const data = response.data.data ?? {}
    const loadedComments = data.comments ?? []
    comments.value = Array.isArray(loadedComments) ? loadedComments : Object.values(loadedComments)
    commentsExists.value = comments.value.length > 0

    partnerUsers.value = data.partner_users ?? []
    canManagePartnerVisibility.value = Boolean(data.can_manage_partner_visibility)
    canWriteComments.value = Boolean(data.can_write_comments ?? is('manager|admin|partner_collab'))
    canUploadCommentFiles.value = Boolean(
        data.can_upload_comment_files ?? is('manager|admin|partner_collab')
    )
    canDeleteCommentFiles.value = Boolean(data.can_delete_comment_files ?? is('manager|admin'))

    const visibilityByComment: Record<number, number[]> = {}
    comments.value.forEach((comment: any) => {
        visibilityByComment[comment.id] = Array.isArray(comment.visible_partner_user_ids)
            ? comment.visible_partner_user_ids
            : []
    })
    commentVisibilitySelection.value = visibilityByComment
}

async function updateCommentVisibility(commentId: number) {
    await apiRequest('patch', '/contacts/' + props.contact.id + '/comments/' + commentId + '/visibility', {
        partner_user_ids: commentVisibilitySelection.value[commentId] ?? []
    })
}

function openVisibilityPopover(event: Event, commentId: number) {
    activeVisibilityCommentId.value = commentId
    visibilityPopover.value?.toggle(event)
}

async function updateActiveCommentVisibility() {
    if (activeVisibilityCommentId.value === null) {
        return
    }

    await updateCommentVisibility(activeVisibilityCommentId.value)
}

async function deleteFile(commentId: number, fileId: number) {
    confirm.require({
        message: 'Ar tikrai norite ištrinti šį failą?',
        header: 'Patvirtinimas',
        icon: 'pi pi-exclamation-triangle',
        rejectProps: {
            label: 'Ne',
            severity: 'secondary',
            outlined: true
        },
        acceptProps: {
            label: 'Taip'
        },
        accept: async () => {
            await apiRequest(
                'delete',
                '/contacts/' + props.contact.id + '/comments/' + commentId + '/files/' + fileId + '/delete'
            )

            await loadComments()
        }
    })
}

const customUploader = async (
    { files }: { files: File[] },
    commentId: number | string
): Promise<void> => {
    const formData = new FormData()

    files.forEach((file: File) => {
        formData.append('docs[]', file)
    })

    await apiRequest('post', `/contacts/${props.contact.id}/comments/${commentId}/upload`, formData)

    await loadComments()
}

async function openFile(commentId: number, file: CommentFile) {
    await openProtectedFile(
        '/contacts/' + props.contact.id + '/comments/' + commentId + '/files/' + file.id + '/download',
        file.name
    )
}
</script>
